import { useMsal } from '@azure/msal-react';
import { useEffect, useState } from 'react';

interface Props {
  upn: string;
  fullName: string;
  size?: 'sm' | 'md' | 'lg';
}

const sizeClass: Record<NonNullable<Props['size']>, string> = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-12 w-12 text-sm',
  lg: 'h-14 w-14 text-base',
};

function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export function Avatar({ upn, fullName, size = 'md' }: Props) {
  const { instance } = useMsal();
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;

    async function load() {
      const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      if (!account) return;
      try {
        const { accessToken } = await instance.acquireTokenSilent({
          scopes: [import.meta.env.VITE_API_SCOPE],
          account,
        });
        const r = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}/api/users/${encodeURIComponent(upn)}/photo`,
          { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        if (!r.ok) return;
        const data = await r.blob();
        if (cancelled) return;
        url = URL.createObjectURL(data);
        setObjectUrl(url);
      } catch {
        /* fallback a iniciales */
      }
    }

    void load();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [instance, upn]);

  const cls = `grid shrink-0 place-items-center rounded-full bg-ink-800 font-medium tracking-wide text-ink-100 overflow-hidden ${sizeClass[size]}`;

  if (!objectUrl) {
    return <div className={cls}>{initials(fullName)}</div>;
  }

  return (
    <div className={cls}>
      <img src={objectUrl} alt={fullName} className="h-full w-full object-cover" />
    </div>
  );
}
